import { useRef } from "react";
import "./shareOrder.css";
import QRCode from "react-qr-code";
import { useAuth } from "@/_zustand/auth";
import { useShareQrImage } from "@/hooks/useShareQrImage";
import { useSendQrEmail } from "@/hooks/useSendQrEmail";
import { HiddenShareQr } from "@/components/HiddenShareQr";
import { estaVencido } from "@/lib/orderExpiry";

export const ShareOrderModal = ({ order, show, onHide }) => {
  const { user } = useAuth();
  // Defensa en profundidad: la fila de la tabla ya deshabilita el botón que
  // abre este modal para un pedido vencido, pero si de todos modos se abre
  // (p. ej. otro caller futuro), no debe permitir compartir/enviar el QR.
  // Se calcula acá con la misma fuente de verdad (order.pedido_vigente), sin
  // agregar un prop booleano nuevo que pueda desincronizarse.
  const vencido = order != null && estaVencido(order.pedido_vigente);

  // qrRef solo se conserva como fuente de fallback (QR visible) para
  // HiddenShareQr.toPngBlob, si el nodo oculto no estuviera disponible.
  const qrRef = useRef(null);
  const { shareQrRef, isSharing, generateAndShare, shareNativeOrFallback } =
    useShareQrImage({
      pedidoId: order?.pedido_id,
      fallbackText: order?.pedido_id,
    });
  const { email, setEmail, emailError, isSending, sendEmail } =
    useSendQrEmail({
      pedidoId: order?.pedido_id,
      codigoQr: order?.codigo_qr,
      correoInicial: user.usuario_correo,
    });

  const handleCopy = () => {
    if (vencido) return;
    generateAndShare(qrRef.current?.querySelector("svg"));
  };

  // Comparte el QR real (selector nativo del sistema, no una integración
  // directa con WhatsApp: la Web Share API no permite dirigir el contenido a
  // una app específica). Ver src/hooks/useShareQrImage.js.
  const handleShare = () => {
    if (vencido) return;
    shareNativeOrFallback(qrRef.current?.querySelector("svg"));
  };

  // El modal permanece abierto hasta que la petición resuelva: si se cerrara
  // de inmediato (comportamiento anterior) el error inline y el estado de
  // carga no tendrían dónde mostrarse. Solo se cierra tras un envío exitoso.
  const handleSendEmail = async () => {
    if (vencido) return;
    const success = await sendEmail();
    if (success) onHide();
  };

  if (show && order)
    return (
      <div className="order-modal-overlay">
        <div className="order-modal-content">
          <button className="order-close-button" onClick={onHide}>
            &times;
          </button>
          <h2>Compartir datos de esta compra</h2>
          <div className="order-modal-body">
            <div className="order-content-flex">
              <div ref={qrRef} className="order-qr-section">
                {vencido ? (
                  <p className="order-qr-vencido">
                    Este código ya venció, no se puede compartir
                  </p>
                ) : order.codigo_qr ? (
                  <QRCode
                    size={150}
                    style={{ height: "auto", width: "auto" }}
                    value={order.codigo_qr}
                    // Sin viewBox: react-qr-code lo sobreescribe siempre con
                    // "0 0 {módulos} {módulos}", así que pasarlo acá era
                    // código muerto que no cambiaba nada.
                  />
                ) : null}
              </div>
              {vencido ? null : (
                <HiddenShareQr ref={shareQrRef} value={order.codigo_qr} />
              )}

              <div className="order-column-right">
                <div className="order-modal-text-left">
                  <p>
                    <strong>ID de compra:</strong>{" "}
                    <span>{order.pedido_id}</span>
                  </p>
                  <p>Código válido hasta {order.pedido_fecha}</p>
                </div>
                <div className="order-actions">
                  <button
                    className="order-whatsapp-btn"
                    onClick={handleShare}
                    disabled={isSharing || vencido}
                  >
                    Compartir <i className="fas fa-share-alt"></i>
                  </button>
                  <button
                    className="order-copy-btn"
                    onClick={() => handleCopy()}
                    disabled={isSharing || vencido}
                  >
                    Copiar código <i className="far fa-copy"></i>
                  </button>
                </div>
              </div>
            </div>
            <p className="order-divider-text">O si lo prefieres, puedes</p>
            <div className="order-email-section">
              <input
                type="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? "share-order-email-error" : undefined}
              />
              <button
                className="order-send-btn"
                onClick={handleSendEmail}
                disabled={isSending || vencido}
              >
                {isSending ? "Enviando…" : "Enviar código"}
              </button>
            </div>
            {emailError ? (
              <p id="share-order-email-error" className="text-danger font12 mb-0">
                {emailError}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
};
